/**
 * Retrieval Table Component
 * 
 * This component displays a table of retrieval requests with
 * filtering, sorting, and action capabilities.
 */

import React from 'react';
import { useApp } from '../../context/AppContext';
import { useTranslations } from '../../context/TranslationsContext';

const RetrievalTable = ({ requests, onViewDetails, onApprove, onStartDelivery, onComplete }) => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(price);
  };

  const createGoogleMapsLink = (address) => {
    if (!address) return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      retrieval_requested: 'bg-yellow-100 text-yellow-800',
      retrieval_approved: 'bg-blue-100 text-blue-800',
      retrieval_delivering: 'bg-purple-100 text-purple-800',
      retrieval_completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>{t.orderNumber}</th>
            <th>{t.customerName}</th>
            <th>{t.phone}</th>
            <th>{t.status}</th>
            <th>{t.deliveryAddress}</th>
            <th>{t.createdAt}</th>
            <th>{t.actions}</th>
          </tr>
        </thead>
        <tbody>
          {requests.map(request => (
            <tr key={request.id}>
              <td>#{request.short_id || request.id?.substring(0, 8)}</td>
              <td>{request.customer_name}</td>
              <td>{request.customer_phone}</td>
              <td>
                <span className={`status-badge ${getStatusColor(request.status)}`}>
                  {t[request.status]}
                </span>
              </td>
              <td>
                {request.retrieval_delivery_address ? (
                  <a 
                    href={createGoogleMapsLink(request.retrieval_delivery_address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="address-link"
                    style={{
                      color: '#1976d2',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    {request.retrieval_delivery_address} 📍
                  </a>
                ) : (
                  t.notSpecified
                )}
              </td>
              <td>{formatDate(request.retrieval_requested_at || request.created_at)}</td>
              <td>
                <div className="action-buttons">
                  <button
                    onClick={() => onViewDetails(request)}
                    className="btn-secondary btn-sm"
                  >
                    {t.viewDetails}
                  </button>
                  
                  {request.status === 'retrieval_requested' && (
                    <button
                      onClick={() => {
                        const notes = prompt(t.adminNotesOptional + ':');
                        if (notes !== null) {
                          onApprove(request.id, notes);
                        }
                      }}
                      className="btn-success btn-sm"
                    >
                      {t.approve}
                    </button>
                  )}
                  
                  {request.status === 'retrieval_approved' && (
                    <button
                      onClick={() => {
                        const notes = prompt(t.adminNotesOptional + ':');
                        if (notes !== null) {
                          onStartDelivery(request.id, notes);
                        }
                      }}
                      className="btn-primary btn-sm"
                    >
                      {t.startDelivery}
                    </button>
                  )}
                  
                  {request.status === 'retrieval_delivering' && (
                    <button
                      onClick={() => {
                        const notes = prompt(t.adminNotesOptional + ':');
                        if (notes !== null) {
                          onComplete(request.id, notes);
                        }
                      }}
                      className="btn-success btn-sm"
                    >
                      {t.complete}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RetrievalTable;
